import React from 'react';
import { motion } from 'framer-motion';
import { getStats, StatsResponse } from '../services/api';

// HeroStats — full-width stat boxes beneath the hero CTA. Site-wide counters
// as discrete cards spread across the hero's workable width. Silently hides
// on failure or empty data so a broken /stats endpoint never leaves debris
// on the marketing page.
const HeroStats: React.FC = () => {
  const [stats, setStats] = React.useState<StatsResponse | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getStats();
        if (!cancelled) setStats(s);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (failed || !stats) return null;
  const empty = stats.stacksCompleted === 0 && stats.lightFrames === 0 && stats.integrationSeconds === 0;
  if (empty) return null;

  const items: Array<{ value: string; label: string }> = [
    { value: stats.stacksCompleted.toLocaleString(), label: 'stacks' },
    { value: formatHours(stats.integrationSeconds), label: 'integration' },
    { value: stats.lightFrames.toLocaleString(), label: 'frames' },
    { value: stats.astronomers.toLocaleString(), label: stats.astronomers === 1 ? 'astronomer' : 'astronomers' },
  ];
  // Cadets = signed up but hasn't stacked yet. Only show when any exist —
  // zero cadets on the homepage reads as "nobody's signing up", worse than
  // omitting. Field is absent on cached rows written before the stats-
  // updater learned about it (treated as 0 by the ?? below).
  const cadets = stats.cadets ?? 0;
  if (cadets > 0) {
    items.push({
      value: cadets.toLocaleString(),
      label: cadets === 1 ? 'cadet' : 'cadets',
    });
  }
  if (stats.anonymousShares > 0) {
    items.push({
      value: stats.anonymousShares.toLocaleString(),
      label: stats.anonymousShares === 1 ? 'preview' : 'previews',
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="mt-10 w-full"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-aurora-400 opacity-70 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-aurora-400" />
        </span>
        <span className="uppercase tracking-widest text-white/45 text-[11px] font-mono">
          Since first light
        </span>
      </div>
      <div
        className={`grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3 ${
          items.length >= 6 ? 'md:grid-cols-6'
            : items.length === 5 ? 'md:grid-cols-5'
            : 'md:grid-cols-4'
        }`}
      >
        {items.map((it) => (
          <StatBox key={it.label} value={it.value} label={it.label} />
        ))}
      </div>
    </motion.div>
  );
};

const StatBox: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur px-3 py-2.5 md:px-5 md:py-4">
    <div className="text-white font-semibold font-mono tabular-nums text-lg md:text-2xl leading-none truncate">
      {value}
    </div>
    <div className="mt-1.5 text-white/50 text-[10px] md:text-xs uppercase tracking-wider truncate">
      {label}
    </div>
  </div>
);

// Match the full widget's hoursFmt but drop the leading-zero minute padding —
// "89h 4m" reads cleaner than "89h 04m" at this size.
const formatHours = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h.toLocaleString()}h ${m}m`;
};

export default HeroStats;
